import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import bannerImg from "../assets/images/shutterstock_2690045837.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faArrowRight,
  faArrowRightLong,
  faAward,
  faBath,
  faBed,
  faCommentAlt,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faQuoteLeft,
  faQuoteRight,
  faRulerCombined,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import avatar1 from "../assets/images/avatar1.png";
import avatar2 from "../assets/images/avatar3.png";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import { motion } from "framer-motion";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import globalState from "../utils/globalState";
import { useUserParams } from "../hooks/useUserParams";
import { buttonAnimation, fadeUp, stagger } from "../functions/animations";
import Contact from "./contact.component";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";

function toDisplayText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    if (value.highlight != null) return toDisplayText(value.highlight);
    if (typeof value.text === "string") return value.text;
    if (typeof value.value === "string") return value.value;
    if (Array.isArray(value.content)) {
      return value.content.map(toDisplayText).filter(Boolean).join(" ");
    }
    for (const nested of Object.values(value)) {
      const text = toDisplayText(nested);
      if (text) return text;
    }
  }
  return "";
}

function toImageSrc(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.url || value.src || value.image || null;
  }
  return null;
}

function normalizeTestimonialsList(rawTestimonials) {
  let list;
  if (Array.isArray(rawTestimonials)) {
    list = rawTestimonials;
  } else if (typeof rawTestimonials === "string") {
    try {
      const parsed = JSON.parse(rawTestimonials);
      return normalizeTestimonialsList(parsed);
    } catch {
      return [];
    }
  } else if (rawTestimonials && typeof rawTestimonials === "object") {
    if (Array.isArray(rawTestimonials.items)) {
      list = rawTestimonials.items;
    } else if (Array.isArray(rawTestimonials.data)) {
      list = rawTestimonials.data;
    } else {
      list = Object.values(rawTestimonials).filter(
        (item) => item != null && typeof item !== "string"
      );
    }
  } else {
    return [];
  }

  return list
    .map((item, index) => {
      if (item == null) return null;
      if (typeof item === "string") {
        return {
          name: `Testimonial ${index + 1}`,
          quote: item,
          image: null,
          location: "",
        };
      }
      if (typeof item !== "object") return null;

      const quote = toDisplayText(
        item.quote ?? item.description ?? item.highlight ?? item.text
      );
      const name =
        toDisplayText(item.name) || `Testimonial ${index + 1}`;
      const location = toDisplayText(
        item.location ?? item.role ?? item.title
      );

      return {
        name,
        quote,
        image: toImageSrc(item.image ?? item.img),
        location,
      };
    })
    .filter((item) => item && (item.quote || item.name));
}

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const loaded = useRef(false);
  const {
    getCurrentAgentName,
    getCurrentAbout,
    getCurrentHeadshot,
    getCurrentTestimonials,
  } = useUserParams();

  const rawTestimonials = getCurrentTestimonials();
  const testimonials = useMemo(
    () => normalizeTestimonialsList(rawTestimonials),
    [rawTestimonials]
  );

  // State
  // const [listings, setListings] = useState([]); //for production
  const [listings, setListings] = useState(data.value); //for local testing
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=(ListOfficeKey eq '${constants.OFFICE_ID}' and StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=(ListOfficeKey(S)eq(S)\'${constants.OFFICE_ID}\'(S)and(S)StandardStatus(S)eq(S)\'Active\')(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;


        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "local") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `/odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      userId: urlParams.get("userId"),
    };
  };

  // Function to fetch template settings from MongoDB
  const fetchTemplateSettings = async (userId) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(
        `https://agent.proagentsolutions.com/MongoDB/GetTemplateSettingsByUserId?userId=${userId}&apiKey=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const templateSettings = await response.json();
      console.log("Template settings fetched:", templateSettings);

      if (templateSettings) {
        const settingsData = templateSettings.data || templateSettings;

        const mappedUserData = {
          userId: userId,
          agentName: settingsData.agentName || settingsData.AGENT_NAME,
          agentTitle: settingsData.agentTitle || settingsData.AGENT_TITLE,
          agentNumber: settingsData.agentNumber || settingsData.AGENT_NUMBER,
          agentEmail: settingsData.agentEmail || settingsData.AGENT_EMAIL,
          brokerage: settingsData.brokerage || settingsData.BROKERAGE,
          ...settingsData,
        };

        globalState.setUserData(mappedUserData);

        console.log("Template settings data:", settingsData);
        console.log("Template settings mapped to user data:", mappedUserData);
      }

      return templateSettings;
    } catch (error) {
      console.error("Error fetching template settings:", error);
      return null;
    }
  };

  // Load template settings when userId is present in URL
  useEffect(() => {
    const urlParams = getURLParameters();
    if (urlParams.userId) {
      globalState.setUserId(urlParams.userId);
      fetchTemplateSettings(urlParams.userId).then(() => {
        console.log(
          "Global State Updated with Template Settings:",
          globalState.getDebugInfo()
        );
      });
      console.log("Global State Updated:", globalState.getDebugInfo());
    }
  }, []);

  // Data fetching effect with React Snap compatibility
  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    const isReactSnapEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const isBrowser = typeof window !== "undefined";

    if (isBrowser && !isReactSnapEnv && !loaded.current) {
      getData()
        .then((data) => {
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
      loaded.current = true;
    }
  }, []);

  function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, "");
    str = str.toLowerCase();
    str = str
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return str;
  }

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="mx-auto w-full">
          <div className="relative h-screen flex items-center justify-center">

            {/* Image */}
            <Swiper
              modules={[EffectFade, Autoplay]}
              effect="fade"
              centeredSlides
              fadeEffect={{
                crossFade: true,
              }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              speed={1500}
              loop
              className="absolute inset-0 h-full w-full z-0"
              onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            >
              {constants.HEROIMAGES.map((image, index) => (
                <SwiperSlide key={index}>
                  <motion.img
                    src={image}
                    alt={`Hero ${index + 1}`}
                    animate={{
                      scale: activeIndex === index ? 1.15 : 1,
                    }}
                    transition={{ duration: 4 }}
                    className="h-full w-full object-cover"
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <div class="absolute inset-0 bg-black/10 z-10"></div>
            {/* Left Black Gradient Overlay */}
            <div className="absolute inset-0 z-20 bg-gradient-to-b from-black/60 via-transparent to-black/50" />

            {/* Floating Content Card */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="absolute text-center max-w-2xl py-8 px-6 md:p-10 z-20 text-white"
            >
              <div className="">
                <span className="uppercase tracking-[0.4em] text-lg font-semibold">
                  Trusted Real Estate
                </span>
              </div>
              <h1 className="mt-8 font-heading2 text-6xl leading-[0.95] text-white">
                Helping You Find Home
              </h1>

              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <Link
                  to="/listings"
                  className="btn-primary-outline"
                >
                  Explore Listings
                </Link>

                <Link
                  to="/contact"
                  className="btn-primary-outline"
                >
                  Contact
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section >

      {/* About Section */}
      <section className="relative overflow-hidden bg-light py-24 md:py-32 lg:py-40">

        {/* Background Text */}
        <h2
          className="pointer-events-none absolute top-16 left-1/2 -translate-x-1/2 whitespace-nowrap font-serif text-4xl md:text-7xl lg:text-[90px] xl:text-[170px] uppercase tracking-[0.25em] lg:tracking-[0.35em] text-heading"
        >
          {getCurrentAgentName()}
        </h2>

        <div className="relative mx-auto max-w-[1300px] px-5 sm:px-8">
          <div
            className="grid grid-cols-1 lg:grid-cols-12 items-center"
          >
            {/* LEFT CONTENT */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .8 }}
              className="lg:col-span-7 order-2 lg:order-1 lg:pr-24 relative z-10"
            >
              {/* Label */}
              <div className="mb-8 md:mb-10 flex items-center gap-4 md:gap-6">

                <span className="h-px w-14 md:w-24 bg-black/40" />

                <span
                  className="text-sm uppercase tracking-[0.45em] md:tracking-[0.55em] text-black/60"
                >
                  About Me
                </span>
              </div>

              <h3
                className="max-w-3xl font-heading2 text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-neutral-800"
              >
                Creating
                <br />
                Timeless Real Estate
                <br />
                Experiences.
              </h3>

              <p
                className="mt-8 max-w-xl text-base leading-8 md:leading-9 text-gray-600 whitespace-pre-line"
              >
                {getCurrentAbout()}
              </p>

              <Link
                to="/about"
                className="btn-primary-outline-accent mt-8"
              >
                Discover More
              </Link>

              {/* STATS */}
              <div
                className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-10"
              >
                {[
                  ["15+", "Years Experience"],
                  ["250+", "Homes Sold"],
                  ["98%", "Client Trust"]
                ].map((item, index) => (
                  <div
                    key={index}
                    className="border-t border-accent/60 pt-6 md:pt-8"
                  >
                    <p
                      className="font-heading2 text-4xl md:text-5xl text-neutral-800"
                    >
                      {item[0]}
                    </p>

                    <span
                      className="mt-3 md:mt-4 block text-[9px] md:text-[10px] uppercase tracking-[0.35em] md:tracking-[0.45em] text-black/60"
                    >
                      {item[1]}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* RIGHT IMAGE */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .8 }}
              className="lg:col-span-5 order-1 lg:order-2 relative h-[500px] sm:h-[600px] lg:h-[760px] mb-16 lg:mb-0"
            >
              {/* Frame */}
              <div
                className="absolute -top-5 -right-5 md:-top-8 md:-right-8 h-full w-full border border-accent/60"
              />

              <img
                src={getCurrentHeadshot()}
                alt={getCurrentAgentName()}
                className="relative z-10 h-full w-full object-cover"
              />

              {/* Overlay */}
              <div
                className="absolute inset-0 z-20 bg-gradient-to-t from-black/60 via-transparent to-transparent"
              />

              {/* Vertical Label */}
              <div
                className="absolute right-5 md:right-8 top-10 md:top-16 z-30 rotate-90 origin-right text-[9px] md:text-[10px] uppercase tracking-[0.55em] md:tracking-[0.7em] text-white/80"
              >
                Luxury Real Estate
              </div>

              {/* Bottom Info */}
              <div
                className="absolute bottom-8 md:bottom-12 left-8 md:left-12 z-30 text-white"
              >
                <p
                  className="font-heading2 text-6xl md:text-8xl leading-none"
                >
                  15+
                </p>

                <p
                  className="mt-3 md:mt-4 text-[9px] md:text-[10px] uppercase tracking-[0.45em] md:tracking-[0.55em] text-white/70"
                >
                  Years Of Excellence
                </p>
              </div>

              {/* Corner Accent */}
              <div
                className="absolute bottom-0 right-0 z-40 h-20 w-20 md:h-28 md:w-28 border-l border-t border-white/40"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Listings Section */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: .2 }}
        className="relative overflow-hidden bg-neutral-800 text-white"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-28 lg:px-16">
          {/* HEADER */}
          <div
            className="mb-20 grid grid-cols-1 lg:grid-cols-2 items-end gap-10"
          >
            <div>
              <motion.p
                variants={fadeUp}
                className="text-sm uppercase tracking-[0.7em] text-white/40 mb-6"
              >
                Featured Properties
              </motion.p>

              <motion.h2
                variants={fadeUp}
                className="font-heading2 text-5xl lg:text-6xl leading-[1.05]"
              >
                Homes
                <br />
                Beyond
                <br />
                Ordinary
              </motion.h2>
            </div>
            <div
              className="lg:text-right"
            >
              <p
                className="max-w-md lg:ml-auto text-sm leading-8 text-white/50"
              >
                Discover exceptional residences selected
                for architectural beauty, location,
                and timeless value.
              </p>
            </div>
          </div>

          {/* LISTINGS */}
          <motion.div variants={fadeUp}>
            <Swiper
              modules={[Autoplay]}
              spaceBetween={35}
              slidesPerView={1}
              loop
              autoplay={{
                delay: 4500,
                disableOnInteraction: false
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1.2
                },
                900: {
                  slidesPerView: 2
                },
                1280: {
                  slidesPerView: 3
                }
              }}
            >
              {
                listings?.slice(0, 6).map((prop) => (
                  <SwiperSlide key={prop.ListingKey}>
                    <Link
                      to={`/listing/${prop.ListingKey}`}
                      className="group block"
                    >
                      <div
                        className="relative h-[520px] overflow-hidden"
                      >
                        {/* IMAGE */}
                        <img
                          // src={
                          //   formatted[prop.ListingKey]?.find((img) => img.Order === 0)?.MediaURL ??
                          //   formatted[prop.ListingKey]?.[0]?.MediaURL ??
                          //   placeholder
                          // }
                          src={prop.MainImgUrl} //dummy image
                          alt={`${prop.StreetNumber} ${prop.StreetName}`}
                          className="absolute h-full w-full object-cover transition duration-[1200ms] group-hover:scale-110"
                        />

                        {/* OVERLAY */}
                        <div
                          className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"
                        />

                        {/* TOP LABEL */}
                        <div
                          className="absolute top-7 left-7"
                        >
                          <span
                            className="border border-white/40 px-5 py-3 text-[10px] uppercase tracking-[0.4em] bg-accent/60 backdrop-blur"
                          >
                            Exclusive
                          </span>
                        </div>

                        {/* PRICE */}
                        <div
                          className="absolute bottom-28 left-8"
                        >
                          <p
                            className="font-serif text-4xl"
                          >
                            ${prop.ListPrice?.toLocaleString()}
                          </p>
                        </div>

                        {/* DETAILS */}
                        <div
                          className="absolute bottom-8 left-8 right-8 flex justify-between items-end"
                        >
                          <div>
                            <p
                              className="text-xs uppercase tracking-[0.35em] text-white/60"
                            >
                              {prop.CityRegion ?? prop.CrossStreet}
                            </p>
                            <h3
                              className="mt-3 font-serif text-2xl"
                            >
                              {prop.StreetNumber}
                              {" "}
                              {prop.StreetName}
                            </h3>
                          </div>

                          <div
                            className="h-12 w-12 rounded-full border border-white/40 flex items-center justify-center transition group-hover:bg-white group-hover:text-black"
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </SwiperSlide>
                ))
              }
            </Swiper>
          </motion.div>

          {/* CTA */}
          <div
            className="mt-20 flex justify-center"
          >
            <Link
              to="/listings"
              className="btn-primary-outline"
            >
              Explore All Properties
            </Link>
          </div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <section
        className="relative overflow-hidden bg-cover bg-center py-32"
        style={{
          backgroundImage: `url(${bannerImg})`
        }}
      >
        <div className="absolute inset-0 bg-black/30" />

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-16">
          {/* Heading */}
          <div className="mb-16 flex items-end justify-between">
            <div>
              <motion.p
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="mb-5 text-sm uppercase tracking-[0.6em] text-white"
              >
                Testimonials
              </motion.p>

              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="max-w-3xl font-heading2 text-5xl leading-tight text-white lg:text-6xl"
              >
                Stories From
                <br />
                Our Clients
              </motion.h2>
            </div>

            <div className="hidden gap-4 lg:flex">
              <button className="testimonial-prev flex h-14 w-14 items-center justify-center rounded-full bg-white/30 backdrop-blur-md transition hover:bg-white/20">
                <FontAwesomeIcon icon={faAngleLeft} className="text-white fa-lg" />
              </button>

              <button className="testimonial-next flex h-14 w-14 items-center justify-center rounded-full bg-white/30 backdrop-blur-md transition hover:bg-white/20">
                <FontAwesomeIcon icon={faAngleRight} className="text-white fa-lg" />
              </button>
            </div>
          </div>

          {/* Cards */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Swiper
              modules={[Navigation, Autoplay]}
              navigation={{
                prevEl: ".testimonial-prev",
                nextEl: ".testimonial-next",
              }}
              spaceBetween={30}
              slidesPerView={1}
              loop
              autoplay={{
                delay: 5000,
                disableOnInteraction: false
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1.2
                },
                1024: {
                  slidesPerView: 2
                },
                1280: {
                  slidesPerView: 3
                }
              }}
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <div className="group h-full bg-white/90 backdrop-blur-xl p-8 lg:p-10 transition duration-500 hover:-translate-y-3">

                    <div className="mb-5 text-4xl leading-none text-accent">
                      <FontAwesomeIcon icon={faQuoteLeft} />
                    </div>

                    <p className="min-h-[140px] font-serif text-xl leading-relaxed text-gray-800">
                      {testimonial.quote}
                    </p>

                    <div className="mt-5 h-px bg-black/10" />

                    <div className="mt-8 flex items-center gap-5">

                      {testimonial.image ? (
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="h-16 w-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-900 font-serif text-white">
                          {testimonial.name
                            .split(" ")
                            .map(x => x[0])
                            .slice(0, 2)
                            .join("")
                          }
                        </div>
                      )}

                      <div>
                        <h3 className="text-sm uppercase tracking-[0.3em] text-neutral-900">
                          {testimonial.name}
                        </h3>

                        <p className="mt-2 text-xs uppercase tracking-[0.3em] text-black/40">
                          {testimonial.location}
                        </p>
                      </div>

                    </div>

                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section >

      {/* Useful Tools Section */}
      < section className="relative overflow-hidden bg-light" >
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1400px] px-6 py-28 lg:px-16"
        >

          {/* HEADER */}
          <div className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <motion.p
                variants={fadeUp}
                className="mb-5 text-sm uppercase tracking-[0.6em] text-black/40"
              >
                Real Estate Resources
              </motion.p>

              <motion.h2
                variants={fadeUp}
                className="font-heading2 text-5xl lg:text-6xl leading-none text-neutral-800"
              >
                Tools For
                <br />
                Smarter Decisions
              </motion.h2>
            </div>

            <p className="max-w-md text-sm leading-7 text-black/50">
              Access professional tools designed to help you understand your home's value,
              financing options, and market opportunities.
            </p>
          </div>

          {/* FEATURE GRID */}
          <motion.div
            variants={fadeUp}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6"
          >
            {/* MAIN FEATURE */}
            <Link
              to="/calculator"
              className="group relative lg:col-span-7 h-[287px] lg:h-[600px] overflow-hidden"
            >
              <img
                src={calc}
                alt="Mortgage Calculator"
                className="absolute inset-0 h-full w-full object-cover transition duration-1000 group-hover:scale-110"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

              <div className="absolute left-8 bottom-5 lg:left-10 lg:bottom-10 text-white">
                <span className="hidden lg:block text-xs uppercase tracking-[0.5em] text-white/60">
                  Featured Tool
                </span>

                <h3 className="lg:mt-5 font-serif text-3xl lg:text-5xl">
                  Mortgage
                  <br />
                  Calculator
                </h3>

                <p className="mt-3 lg:mt-5 max-w-md text-sm leading-7 text-white/70">
                  Plan your purchase with accurate payment estimates and financing insights.
                </p>

                <div className="mt-3 lg:mt-8 flex items-center gap-5 text-xs uppercase tracking-[0.4em]">
                  Explore Tool
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 transition group-hover:bg-white group-hover:text-black">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                </div>
              </div>
            </Link>

            {/* SMALL CARDS */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              {[
                {
                  title: "Market Reports",
                  description: "Stay updated with local trends and pricing insights.",
                  image: report,
                  link: "/real-estate-reports"
                },
                {
                  title: "Home Evaluation",
                  description: "Discover your property's current market value.",
                  image: house,
                  link: "/evaluation"
                }
              ].map((card, index) => (

                <Link
                  key={index}
                  to={card.link}
                  className="group relative h-[287px] overflow-hidden"
                >

                  <img
                    src={card.image}
                    alt={card.title}
                    className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 bg-black/50" />

                  <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
                    <h3 className="font-serif text-3xl">
                      {card.title}
                    </h3>

                    <p className="mt-3 text-sm text-white/70">
                      {card.description}
                    </p>

                    <div className="mt-5 text-[10px] uppercase tracking-[0.4em]">
                      Discover
                      <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </section >

      <Contact />
    </>
  );
}
